import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Heart, Check, Loader2, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESETS = [5, 10, 15];

export default function TipPage() {
  const [params] = useSearchParams();
  const bookingId = params.get("booking");
  const tipId = params.get("tip");

  // Result mode (returning from SumUp) vs selection mode
  if (tipId) return <TipResult tipId={tipId} />;
  return <TipSelect bookingId={bookingId} />;
}

const TipSelect = ({ bookingId }) => {
  const [amount, setAmount] = useState(10);
  const [custom, setCustom] = useState("");
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);

  const effective = custom !== "" ? parseFloat(custom.replace(",", ".")) : amount;

  const pay = async () => {
    setError(null);
    const val = Math.round((effective || 0) * 100) / 100;
    if (!val || val < 1) { setError("Choisissez un montant d'au moins 1 €."); return; }
    setPaying(true);
    try {
      const r = await axios.post(`${API}/tips/create`, {
        booking_id: bookingId,
        amount: val,
        origin: window.location.origin,
      });
      if (r.data.hosted_checkout_url) { window.location.assign(r.data.hosted_checkout_url); return; }
      setError("Impossible de démarrer le paiement. Réessayez.");
      setPaying(false);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      setError(status && status >= 500 ? "Service momentanément indisponible. Réessayez dans un instant." : (typeof detail === "string" && detail.length < 180 ? detail : "Une erreur est survenue."));
      setPaying(false);
    }
  };

  if (!bookingId) {
    return (
      <Shell>
        <p className="text-white/70 text-sm" data-testid="tip-no-booking">Lien invalide. Merci de utiliser le lien reçu après votre course.</p>
        <Link to="/" className="text-[#D4AF37] text-sm mt-4 inline-flex items-center gap-2"><ArrowLeft size={14} /> Retour à l'accueil</Link>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center mx-auto mb-5">
        <Heart size={24} className="text-[#D4AF37]" />
      </div>
      <h1 className="text-2xl sm:text-3xl font-serif text-white text-center">Merci pour votre confiance</h1>
      <p className="text-white/60 text-sm text-center mt-2 mb-7">
        Si vous avez apprécié la course, vous pouvez laisser un pourboire. C'est entièrement libre — même 1 € fait plaisir. 🙏
      </p>

      <div className="grid grid-cols-3 gap-3 mb-4">
        {PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            data-testid={`tip-preset-${p}`}
            onClick={() => { setAmount(p); setCustom(""); }}
            className={`py-4 rounded-2xl text-lg font-bold font-mono-price transition-all border ${custom === "" && amount === p ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-white/5 text-white border-white/15 hover:border-[#D4AF37]/50"}`}
          >
            {p} €
          </button>
        ))}
      </div>

      <div className="relative mb-6">
        <input
          type="number"
          min="1"
          step="1"
          inputMode="decimal"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          placeholder="Autre montant (ex : 2, 3, 20…)"
          data-testid="tip-custom-input"
          className="w-full bg-white/5 border border-white/15 focus:border-[#D4AF37] rounded-2xl px-4 py-3.5 text-white placeholder-white/35 outline-none transition-colors font-mono-price"
        />
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40">€</span>
      </div>

      {error && <p className="text-red-400 text-sm mb-4 text-center" data-testid="tip-error">{error}</p>}

      <button
        type="button"
        onClick={pay}
        disabled={paying}
        data-testid="tip-pay-btn"
        className="w-full bg-[#D4AF37] hover:bg-[#c6a330] text-black font-bold py-4 rounded-2xl transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
      >
        {paying ? <><Loader2 size={18} className="animate-spin" /> Redirection…</> : `Payer ${(effective && effective >= 1) ? `${Math.round(effective * 100) / 100} €` : ""} par carte`}
      </button>
      <p className="text-[11px] text-white/30 text-center mt-3">Paiement sécurisé par carte via SumUp</p>
    </Shell>
  );
};

const TipResult = ({ tipId }) => {
  const [state, setState] = useState("pending"); // pending | paid | failed
  const [tip, setTip] = useState(null);
  const [tries, setTries] = useState(0);

  const poll = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tips/${tipId}`);
      setTip(r.data);
      if (r.data.status === "paid") setState("paid");
      else if (r.data.status === "failed") setState("failed");
      else setState("pending");
    } catch {
      setState("failed");
    }
  }, [tipId]);

  useEffect(() => { poll(); }, [poll]);
  useEffect(() => {
    if (state !== "pending") return;
    if (tries > 10) return;
    const id = setTimeout(() => { setTries((t) => t + 1); poll(); }, 2000);
    return () => clearTimeout(id);
  }, [state, tries, poll]);

  return (
    <Shell>
      {state === "paid" ? (
        <div className="text-center" data-testid="tip-success">
          <div className="w-16 h-16 rounded-full bg-green-400/15 border border-green-400/40 flex items-center justify-center mx-auto mb-5">
            <Check size={30} className="text-green-400" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif text-white">Merci infiniment ! 🙏</h1>
          <p className="text-white/60 text-sm mt-3">
            Votre pourboire{tip?.amount ? ` de ${tip.amount} €` : ""} a bien été reçu. Cela compte beaucoup — au plaisir de vous conduire à nouveau.
          </p>
          <Link to="/" className="text-[#D4AF37] text-sm mt-6 inline-flex items-center gap-2" data-testid="tip-home-link"><ArrowLeft size={14} /> Retour à l'accueil</Link>
        </div>
      ) : state === "failed" ? (
        <div className="text-center" data-testid="tip-failed">
          <h1 className="text-2xl font-serif text-white">Paiement non abouti</h1>
          <p className="text-white/60 text-sm mt-3">Le pourboire n'a pas pu être confirmé. Vous pouvez réessayer depuis le lien reçu.</p>
          <Link to="/" className="text-[#D4AF37] text-sm mt-6 inline-flex items-center gap-2"><ArrowLeft size={14} /> Retour à l'accueil</Link>
        </div>
      ) : (
        <div className="text-center" data-testid="tip-pending">
          <Loader2 size={30} className="text-[#D4AF37] animate-spin mx-auto mb-4" />
          <p className="text-white/70 text-sm">Confirmation du paiement en cours…</p>
        </div>
      )}
    </Shell>
  );
};

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-12">
    <div className="w-full max-w-md bg-gradient-to-b from-white/[0.06] to-transparent border border-white/10 rounded-3xl p-7 sm:p-9">
      {children}
    </div>
  </div>
);
